import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  useTheme,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  MoreVert as MoreIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  OndemandVideo as VideoIcon,
} from '@mui/icons-material';
import StatusBadge from './StatusBadge';
import PipelineEndpoints from './PipelineEndpoints';
import ErrorSimulationCards from './ErrorSimulationCards';
import CMSDControls from './CMSDControls';
import { formatBitrate } from '../utils/format';

function PipelineCard({ pipeline }) {
  const navigate = useNavigate();
  const isS3 = pipeline.type === 's3';
  const isTouchstream = pipeline.type === 'touchstream';
  const isRunning = pipeline.status === 'running';
  const isActive = isRunning || isS3; // S3 pipelines are always available
  // Touchstream-only pipelines have no infra to "run" but expose a manifest URL
  // that should display like the S3 URL.
  const showStreamUrls = isActive || (isTouchstream && !!pipeline.hls_url);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const healthPercentage = pipeline.metrics?.health_status === 'healthy' ? 85 :
                           pipeline.metrics?.health_status === 'warning' ? 60 : 30;

  const hasStreamUrls = pipeline.hls_url || pipeline.dash_url;

  const handlePlayStream = () => {
    navigate(`/player/${pipeline.id}`);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.9) 0%, rgba(22, 33, 62, 0.9) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        border: isDark ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isActive
            ? isS3
              ? 'linear-gradient(90deg, #9c27b0 0%, #e91e63 100%)'
              : 'linear-gradient(90deg, #00d4ff 0%, #00ff88 100%)'
            : isDark
            ? 'linear-gradient(90deg, #333 0%, #555 100%)'
            : 'linear-gradient(90deg, #e0e0e0 0%, #f5f5f5 100%)',
          boxShadow: isActive && isDark ? '0 0 10px rgba(0, 212, 255, 0.5)' : 'none',
        },
      }}
    >
      <CardContent sx={{ pt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                background: isDark && isActive
                  ? isS3
                    ? 'linear-gradient(45deg, #9c27b0 30%, #ffffff 90%)'
                    : 'linear-gradient(45deg, #00d4ff 30%, #ffffff 90%)'
                  : 'inherit',
                WebkitBackgroundClip: isDark && isActive ? 'text' : 'inherit',
                WebkitTextFillColor: isDark && isActive ? 'transparent' : 'inherit',
                mb: 0.5,
              }}
            >
              {pipeline.name}
            </Typography>
            {pipeline.cost_center && (
              <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.8 }}>
                {pipeline.cost_center}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <StatusBadge status={pipeline.status} />
            {/* Play Button in Header */}
            {hasStreamUrls && (
              <Tooltip title="Play stream">
                <IconButton
                  size="small"
                  onClick={handlePlayStream}
                  sx={{
                    color: isDark ? '#00d4ff' : 'primary.main',
                    background: isDark ? 'rgba(0, 212, 255, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                    '&:hover': {
                      background: isDark ? 'rgba(0, 212, 255, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <PlayIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: isDark ? '#00d4ff' : 'primary.main',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              <MoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          <Chip
            label={pipeline.type.toUpperCase()}
            size="small"
            sx={{
              background: isS3
                ? isDark ? 'rgba(156, 39, 176, 0.1)' : 'rgba(156, 39, 176, 0.1)'
                : isDark ? 'rgba(0, 212, 255, 0.1)' : 'rgba(25, 118, 210, 0.1)',
              color: isS3
                ? isDark ? '#ce93d8' : '#9c27b0'
                : isDark ? '#00d4ff' : '#1976d2',
              border: `1px solid ${isS3
                ? isDark ? 'rgba(156, 39, 176, 0.3)' : 'rgba(156, 39, 176, 0.3)'
                : isDark ? 'rgba(0, 212, 255, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
              fontWeight: 600,
            }}
          />
          {isS3 && (
            <Chip
              icon={<VideoIcon sx={{ fontSize: '16px !important' }} />}
              label="VOD"
              size="small"
              sx={{
                background: isDark ? 'rgba(233, 30, 99, 0.1)' : 'rgba(233, 30, 99, 0.1)',
                color: isDark ? '#f48fb1' : '#e91e63',
                border: `1px solid ${isDark ? 'rgba(233, 30, 99, 0.3)' : 'rgba(233, 30, 99, 0.3)'}`,
                fontWeight: 600,
              }}
            />
          )}
          {isRunning && !isS3 && (
            <Chip
              icon={<TrendingUpIcon sx={{ fontSize: '16px !important' }} />}
              label={`$${pipeline.estimated_hourly_cost || 0}/hr`}
              size="small"
              sx={{
                background: isDark ? 'rgba(255, 170, 0, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                color: isDark ? '#ffaa00' : '#ff9800',
                border: `1px solid ${isDark ? 'rgba(255, 170, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                fontWeight: 600,
              }}
            />
          )}
          {pipeline.creator_username && (
            <Chip
              label={pipeline.creator_username}
              size="small"
              variant="outlined"
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        {/* Performance Metrics - only for running live pipelines (not S3) */}
        {isRunning && !isS3 && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <SpeedIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="textSecondary" fontWeight={600}>
                Performance Metrics
              </Typography>
            </Box>

              <Box
                sx={{
                  background: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                  borderRadius: 2,
                  p: 2,
                  border: isDark ? '1px solid rgba(0, 212, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Input: {formatBitrate(pipeline.metrics?.input_bitrate || 0)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    Output: {formatBitrate(pipeline.metrics?.output_bitrate || 0)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={healthPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      background: healthPercentage > 70
                        ? 'linear-gradient(90deg, #00d4ff 0%, #00ff88 100%)'
                        : healthPercentage > 50
                        ? 'linear-gradient(90deg, #ffaa00 0%, #ff6600 100%)'
                        : 'linear-gradient(90deg, #ff4444 0%, #ff0000 100%)',
                      boxShadow: isDark ? `0 0 8px ${healthPercentage > 70 ? '#00ff88' : healthPercentage > 50 ? '#ffaa00' : '#ff4444'}40` : 'none',
                    },
                  }}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    Health: {pipeline.metrics?.health_status || 'unknown'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {healthPercentage}%
                  </Typography>
                </Box>
              </Box>
          </Box>
        )}

        {/* Endpoints - show for active pipelines (running or S3) */}
        {showStreamUrls && (pipeline.rtmp_url || pipeline.hls_url || pipeline.dash_url) && (
              <Box
                sx={{
                  background: isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 2,
                  p: 2,
                  border: isDark ? '1px solid rgba(0, 212, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                {pipeline.rtmp_url && (
                  <Box mb={pipeline.hls_url || pipeline.dash_url ? 2 : 0}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      {pipeline.rtmp_url.startsWith('srt://') ? 'SRT Endpoint' : 'RTMP Endpoint'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        color: isDark ? '#00d4ff' : '#1976d2',
                        mt: 0.5,
                        p: 1,
                        background: isDark ? 'rgba(0, 212, 255, 0.05)' : 'rgba(25, 118, 210, 0.05)',
                        borderRadius: 1,
                      }}
                    >
                      {pipeline.rtmp_url}
                    </Typography>
                  </Box>
                )}

                {pipeline.hls_url && (
                  <Box mb={pipeline.dash_url ? 2 : 0}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      HLS Endpoint
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        color: isDark ? '#00ff88' : '#4caf50',
                        mt: 0.5,
                        p: 1,
                        background: isDark ? 'rgba(0, 255, 136, 0.05)' : 'rgba(76, 175, 80, 0.05)',
                        borderRadius: 1,
                      }}
                    >
                      {pipeline.hls_url}
                    </Typography>
                  </Box>
                )}

                {pipeline.dash_url && (
                  <Box>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      DASH Endpoint
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        color: isDark ? '#ff9800' : '#f57c00',
                        mt: 0.5,
                        p: 1,
                        background: isDark ? 'rgba(255, 152, 0, 0.05)' : 'rgba(255, 152, 0, 0.05)',
                        borderRadius: 1,
                      }}
                    >
                      {pipeline.dash_url}
                    </Typography>
                  </Box>
                )}
          </Box>
        )}

        {/* Play Stream Button - show for active pipelines (running or S3) and Touchstream-only pipelines with a manifest */}
        {showStreamUrls && hasStreamUrls && (
          <Box mt={2}>
            <Tooltip title="Open stream in video player">
              <Button
                variant="contained"
                startIcon={<VideoIcon />}
                onClick={handlePlayStream}
                fullWidth
                sx={{
                  background: isS3
                    ? isDark
                      ? 'linear-gradient(45deg, #9c27b0 30%, #7b1fa2 90%)'
                      : 'linear-gradient(45deg, #9c27b0 30%, #7b1fa2 90%)'
                    : isDark
                    ? 'linear-gradient(45deg, #00d4ff 30%, #0099cc 90%)'
                    : 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                  '&:hover': {
                    background: isS3
                      ? isDark
                        ? 'linear-gradient(45deg, #7b1fa2 30%, #6a1b9a 90%)'
                        : 'linear-gradient(45deg, #7b1fa2 30%, #6a1b9a 90%)'
                      : isDark
                      ? 'linear-gradient(45deg, #0099cc 30%, #007399 90%)'
                      : 'linear-gradient(45deg, #1565c0 30%, #0d47a1 90%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                {isS3 ? 'Play VOD' : 'Play Stream'}
              </Button>
            </Tooltip>
          </Box>
        )}

        {/* Idle state - only show for non-S3 pipelines that aren't running */}
        {!isActive && (
          <Box
            py={3}
            textAlign="center"
            sx={{
              background: isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
              borderRadius: 2,
              border: isDark ? '1px solid rgba(179, 179, 179, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
            }}
          >
            <IconButton
              sx={{
                background: isDark ? 'rgba(179, 179, 179, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                color: 'text.secondary',
                mb: 1,
              }}
            >
              <PauseIcon />
            </IconButton>
            <Typography variant="body2" color="textSecondary" fontWeight={600}>
              Pipeline is {pipeline.status}
            </Typography>
          </Box>
        )}

        {/* Pipeline Endpoints for Norsk pipelines */}
        <PipelineEndpoints
          pipelineId={pipeline.id}
          pipelineType={pipeline.type}
        />

        {/* Error Simulation Cards for pipelines with Touchstream config */}
        <ErrorSimulationCards pipeline={pipeline} />

        {/* CMSD Controls for pipelines with proxy_url configured */}
        <CMSDControls pipeline={pipeline} />
      </CardContent>
    </Card>
  );
}

export default PipelineCard;